import { activityICS, downloadActivityICS, googleCalendarUrl } from './links'

export type AppleShareOutcome =
  | 'shared'
  | 'unsupported'
  | 'dismissed'
  | 'error'

type IcsActivity = Parameters<typeof downloadActivityICS>[0]

/**
 * Can this client hand an .ics file to the share sheet? On the iOS shell the
 * WKWebView exposes Web Share (iOS 15+), which surfaces the Apple Calendar
 * import for text/calendar files. Desktop Tauri shells do not support file
 * sharing, so callers must guard with this probe.
 */
export function canShareIcsFile(): boolean {
  if (typeof navigator === 'undefined') return false
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean
  }
  if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') {
    return false
  }
  try {
    const probe = new File(['', ], 'kuniri-test.ics', { type: 'text/calendar' })
    return nav.canShare({ files: [probe as unknown as File] })
  } catch {
    return false
  }
}

/** Opens the Google Calendar template link in the system browser or tab. */
export async function openGoogleCalendar(
  activity: IcsActivity,
): Promise<boolean> {
  const url = googleCalendarUrl(activity)
  if (!url) return false
  try {
    const opener = await import('@tauri-apps/plugin-opener')
    await opener.openUrl(url)
    return true
  } catch {
    return false
  }
}

/**
 * Hands the .ics file to the iOS share sheet; the user then taps the Apple
 * Calendar row ("Add to Calendar") which imports the event through EventKit.
 * Requires the Web Share files support probed by `canShareIcsFile()`.
 */
export async function shareIcsWithApple(
  activity: IcsActivity,
): Promise<AppleShareOutcome> {
  const payload = activityICS(activity as Parameters<typeof activityICS>[0])
  if (!payload) return 'error'
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean
  }
  if (!canShareIcsFile()) return 'unsupported'
  const file = new File([payload], `kuniri-${activity.id}.ics`, {
    type: 'text/calendar',
  })
  try {
    await nav.share({
      files: [file],
      title: activity.title,
    } as ShareData)
    return 'shared'
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      return 'dismissed'
    }
    return 'error'
  }
}

/** Transport picker described by D-100/101: shell vs browser. */
export async function addToCalendar(
  activity: IcsActivity,
): Promise<boolean> {
  const { env } = await import('@/lib/config/env')
  if (!env.isTauri()) {
    // Browsers (desktop and phones): the .ics download works and iOS imports
    // it natively.
    return downloadActivityICS(activity)
  }
  if (env.clientPlatform === 'ios') {
    // Never decided silently on iOS: the view opens the Apple/Google chooser.
    return false
  }
  return openGoogleCalendar(activity)
}
